import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Mail, Send, RefreshCw, CheckCircle2, AlertTriangle, Copy } from "lucide-react";
import { toast } from "sonner";

export default function NotificationsPanel() {
  const [health, setHealth] = useState(null);
  const [log, setLog] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const [h, l] = await Promise.all([
          api.get("/email/health"),
          api.get("/email/log?limit=50"),
        ]);
        setHealth(h.data);
        setLog(l.data);
      } catch (e) {
        toast.error("Failed to load email status");
      }
    })();
  }, [tick]);

  const sendTest = async () => {
    if (!testTo.trim()) return toast.error("Enter a recipient email");
    setSending(true);
    try {
      const r = await api.post("/email/test", { to: testTo.trim() });
      if (r.data.ok) toast.success(`Test email sent to ${testTo}`);
      else toast.error("SendGrid rejected the email — see log for details");
      setTick((t) => t + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSending(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  if (!health) return <div className="text-sm font-mono text-slate-500">Loading…</div>;

  const statusBadge = health.configured
    ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Configured</Badge>
    : <Badge className="bg-rose-100 text-rose-800 border-rose-200">Not configured</Badge>;

  return (
    <div className="space-y-6" data-testid="notifications-panel">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-blue-700 flex items-center gap-1.5">
          <Mail className="h-3 w-3" /> Email notifications
        </div>
        <h3 className="font-heading text-xl font-bold text-slate-900 mt-1">SendGrid integration</h3>
        <p className="text-xs text-slate-500 mt-1 max-w-2xl">
          Outbound emails for new tickets, ticket updates &amp; project milestones. Inbound emails to your support address auto-create tickets.
        </p>
      </div>

      {/* Status card */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100 py-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            Outbound status {statusBadge}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => setTick((t) => t + 1)} className="gap-1.5 text-xs">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </CardHeader>
        <CardContent className="p-5 space-y-3 text-xs">
          <Row label="From email" value={health.from_email} mono />
          <Row label="From name" value={health.from_name} />
          <Row label="Reply-to" value={health.reply_to || "—"} mono />
          <div className="grid grid-cols-4 gap-3 pt-3 border-t border-slate-100">
            <Stat label="Sent" value={health.totals.sent} colour="emerald" />
            <Stat label="Errors" value={health.totals.errors} colour="rose" />
            <Stat label="Inbound" value={health.totals.inbound} colour="blue" />
            <Stat label="Total" value={health.totals.total} colour="slate" />
          </div>
        </CardContent>
      </Card>

      {/* Send test */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100 py-3">
          <CardTitle className="text-sm font-heading">Send a test email</CardTitle>
        </CardHeader>
        <CardContent className="p-5 flex gap-2">
          <Input
            data-testid="email-test-to"
            placeholder="recipient@example.com"
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            className="max-w-sm"
          />
          <Button
            data-testid="email-test-send"
            onClick={sendTest}
            disabled={sending}
            className="gap-1.5 bg-blue-600 hover:bg-blue-700"
          >
            <Send className="h-3.5 w-3.5" /> {sending ? "Sending…" : "Send test"}
          </Button>
        </CardContent>
      </Card>

      {/* Inbound setup */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100 py-3">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            Inbound Parse — create tickets from emails
            {health.inbound_secret_set
              ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Secret set</Badge>
              : <Badge className="bg-amber-100 text-amber-800 border-amber-200">No secret</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-4 text-xs">
          <ol className="list-decimal list-inside space-y-3 text-slate-700">
            <li>
              In SendGrid → <b>Settings → Inbound Parse</b> → <b>Add Host &amp; URL</b>.
            </li>
            <li>
              Set the <b>Receiving Domain</b> to a subdomain you control (e.g. <code className="font-mono bg-slate-100 px-1 rounded">parse.xentra.co.uk</code>) and the <b>Destination URL</b> to:
              <div className="flex items-center gap-2 mt-2">
                <code className="font-mono text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-1 flex-1 break-all">
                  {health.inbound_webhook_url || "(set APP_BASE_URL)"}
                </code>
                <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => copy(health.inbound_webhook_url || "")}>
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            </li>
            <li>
              Add an MX record on the chosen subdomain:
              <code className="font-mono text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-1 mt-2 block">
                parse.xentra.co.uk.   IN   MX   10   mx.sendgrid.net.
              </code>
            </li>
            <li>
              Set up email forwarding from <code className="font-mono bg-slate-100 px-1 rounded">support@xentra.co.uk</code> →
              <code className="font-mono bg-slate-100 px-1 rounded ml-1">inbound@parse.xentra.co.uk</code>
              in your Google Workspace / Microsoft 365 admin console. Customers keep emailing <code className="font-mono bg-slate-100 px-1 rounded">support@</code> as normal.
            </li>
            <li>
              (Recommended) Add <code className="font-mono bg-slate-100 px-1 rounded">SENDGRID_INBOUND_SECRET</code> to <code className="font-mono bg-slate-100 px-1 rounded">backend/.env</code> and append <code className="font-mono bg-slate-100 px-1 rounded">?token=&lt;secret&gt;</code> to the Destination URL above. This prevents abuse.
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* Recent log */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100 py-3">
          <CardTitle className="text-sm font-heading">Recent activity (last 50)</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {log.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">No emails yet.</div>
          ) : (
            <table className="w-full text-xs" data-testid="email-log-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-3 py-2 text-left font-mono text-[10px] uppercase tracking-wider text-slate-500">When</th>
                  <th className="px-3 py-2 text-left font-mono text-[10px] uppercase tracking-wider text-slate-500">Status</th>
                  <th className="px-3 py-2 text-left font-mono text-[10px] uppercase tracking-wider text-slate-500">Subject</th>
                  <th className="px-3 py-2 text-left font-mono text-[10px] uppercase tracking-wider text-slate-500">From → To</th>
                </tr>
              </thead>
              <tbody>
                {log.map((row) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono text-slate-500 whitespace-nowrap">
                      {new Date(row.created_at).toLocaleString("en-GB")}
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="px-3 py-2 text-slate-700 max-w-[300px] truncate" title={row.subject}>
                      {row.subject || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-500 max-w-[260px] truncate">
                      {row.from || "—"} → {Array.isArray(row.to) ? row.to.join(", ") : row.to || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const Row = ({ label, value, mono = false }) => (
  <div className="flex items-baseline gap-3">
    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 w-28">{label}</div>
    <div className={`text-slate-800 ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</div>
  </div>
);

const Stat = ({ label, value, colour }) => (
  <div className={`rounded-md border border-${colour}-100 bg-${colour}-50 p-3`}>
    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`text-xl font-heading font-bold text-${colour}-700 mt-1`}>{value}</div>
  </div>
);

const StatusBadge = ({ status }) => {
  const cfg = {
    sent: { c: "emerald", Icon: CheckCircle2 },
    error: { c: "rose", Icon: AlertTriangle },
    skipped_not_configured: { c: "amber", Icon: AlertTriangle },
    inbound_created: { c: "blue", Icon: Mail },
    inbound_reply: { c: "blue", Icon: Mail },
  }[status] || { c: "slate", Icon: Mail };
  const Icon = cfg.Icon;
  return (
    <Badge className={`bg-${cfg.c}-100 text-${cfg.c}-800 border-${cfg.c}-200 gap-1`}>
      <Icon className="h-3 w-3" />
      {status.replace(/_/g, " ")}
    </Badge>
  );
};
